
import {Component, Prop, Vue} from "vue-property-decorator";
import {
  currentUserModule,
  queryModule,
  studyDefinitionModule,
  studyModule,
  studyPatientContext,
  studyPatientModule
} from "@/store";
import {translateEnum, translateFilter} from "@/auxillary/translation";
import {formatDateFilter} from "@/auxillary/date-format";
import VisitContextCache from "@/models/visit.context.cache";
import InstrumentContextCache from "@/models/instrument.context.cache";
import {MdbsCore} from "@/api/tsclient";
import Rights = MdbsCore.Rights;
import PatientQueries from "@/components/query/patient/PatientQueries.vue";
import QueriesMenu from "@/components/query/menu/QueriesMenu.vue";
import {canSeePatientQueries, canSeeQueries, canSeeVisitQueries} from "@/components/query/QueryPermissions";
import {deepCopy} from "@/auxillary";
import IQueryTypeDefinitionDto = MdbsCore.IQueryTypeDefinitionDto;
import QueryTypeDefinitionDto = MdbsCore.QueryTypeDefinitionDto;
import IQueryCreateDto = MdbsCore.IQueryCreateDto;
import QueryCreateMenuItem from "@/components/query/menu/QueryCreateMenuItem.vue";
import QueryCreateDialog from "@/components/query/dialogs/QueryCreateDialog.vue";
import IQueryListResponse = MdbsCore.IQueryListResponse;
import PatientVisitQueriesMenu from "@/components/query/menu/PatientVisitQueriesMenu.vue";
import {canSeeStudyPatientAuditTrail, canSeeVisitAuditTrail} from "@/auxillary/audittrail.permissions";

@Component({
  methods: {canSeeVisitAuditTrail, canSeeStudyPatientAuditTrail},
  components: {
    PatientVisitQueriesMenu, QueriesMenu, QueryCreateMenuItem, QueryCreateDialog, PatientQueries
  },
  filters: {
    translateFilter,
    formatDateFilter
  }
})
export default class PatientView extends Vue {

  @Prop({default: ""}) private patientId: string;

  get patientDefinitionType() {
    return 'Patient';
  }

  get canSeePatient() {
    return currentUserModule.hasRight(Rights.StudyPatientGetByIdCmd);
  }

  get canEditPatient() {
    return currentUserModule.hasRight(Rights.PatientUpdateCmd);
  }

  get canSeeVisit() {
    return currentUserModule.hasRight(Rights.VisitGetAllCmd) || currentUserModule.hasRight(Rights.VisitGetMyCmd)
  }

  get canCreateVisit() {
    return currentUserModule.hasRight(Rights.VisitCreateCmd)
  }

  get canEditVisit() {
    return currentUserModule.hasRight(Rights.VisitUpdateCmd)
  }

  get canCreateInstrument() {
    return currentUserModule.hasRight(Rights.InstrumentCreateCmd)
  }

  get canEditInstrument() {
    return currentUserModule.hasRight(Rights.InstrumentUpdateCmd)
  }

  get patient() {
    return studyPatientContext.studyPatient;
  }

  get birthYear() {
    return studyPatientContext.patientField("birthYear")?.value ?? ""
  }

  get sex() {
    return translateEnum("sex",
        studyPatientContext.patientField("sex")?.value ?? "")
  }

  get visits() {
    return studyPatientContext.visibleVisits ?? []
  }

  get intermediateVisits() {
    return studyPatientContext.visibleIntermediateVisits.sort((x, y) =>
        ((x?.visitDate ? x.visitDate.getTime() : 0) - (y?.visitDate ? y.visitDate.getTime() : 0))) ?? [];
  }

  get intermediateVisitDefinitions() {
    return studyPatientContext.visibleIntermediateVisitDefinitions ?? []
  }

  canCreateNewIntermediateVisit(visit: VisitContextCache) {
    let visitCount = this.intermediateVisits.filter(v => v.definition.definitionType == visit.definition.definitionType).length;
    return !visit.definition.definition?.max || visit.definition.definition?.max > visitCount;
  }

  get standaloneInstruments() {
    return (studyPatientContext.standaloneInstruments ?? []).filter(si => si.isVisible)
  }

  get instituteDisplayName() {
    return studyPatientContext.studyPatient?.studyInstitute?.displayName ?? "";
  }

  get loading() {
    return studyPatientContext.isLoading ||
        studyPatientModule.isLoading ||
        studyModule.isLoading ||
        studyDefinitionModule.isLoading ||
        currentUserModule.isLoading;
  }

  get isPatientComplete() {
    return studyPatientContext.isPatientComplete;
  }

  async mounted() {
    // preselect a institute
    await studyPatientModule.loadById(this.patientId)
    await studyModule.loadById(studyPatientModule?.studyPatient?.studyId ?? "")
    await studyDefinitionModule.loadById(studyModule.study?.studyDefinitionId ?? "")
    await studyPatientContext.loadById(this.patientId)
    if (!this.isPatientComplete)
      await this.editPatient();

    if (this.canSeeQueries) {
      await queryModule.loadPatientMyQueries(this.patientId);
      await queryModule.loadPatientQueries(this.patientId);
    }

    if (canSeeVisitQueries())
      await queryModule.loadPatientVisitsQueries(this.visits.concat(this.intermediateVisits));
  }

  async newVisit(visit: VisitContextCache) {
    if (!visit.isEditable)
      return;

    await this.$router.push({
      name: "visitnew",
      params: {
        patientId: this.patient.id,
        visitDefinitionId: visit.definition.id
      }
    });
  }

  async editVisit(visitId: string) {
    await this.$router.push({
      name: "visitedit",
      params: {
        visitId: visitId
      }
    });
  }

  async openVisit(visit: VisitContextCache) {
    if (visit.isNew) {
      if (!this.canCreateVisit || !visit.isEditable)
        return;
      await this.newVisit(visit)
    } else if (visit.isNotDone) {
      await this.editVisit(visit.id)
    } else {
      await this.$router.push({
        name: "visitinstrument",
        params: {
          patientId: studyPatientModule.studyPatient.id,
          visitId: visit.id
        }
      })
    }
  }

  async openStandaloneInstrument(instrument: InstrumentContextCache) {
    if (instrument.isNew) {
      if (!this.canCreateInstrument)
        return;
      await this.$router.push({
        name: "standaloneinstrumentnew",
        params: {
          patientId: this.patientId,
          instrumentDefinitionId: instrument.definitionId
        }
      })
    } else {
      await this.$router.push({
        name: "standaloneinstrumentedit",
        params: {
          patientId: this.patientId,
          instrumentId: instrument.id
        }
      })
    }
  }

  async editPatient() {
    await this.$router.push({
      name: "patientedit",
      params: {patientId: this.patientId}
    })
  }

  // QUERIES
  showCreateQuery = false;
  createQueryDefinition: IQueryTypeDefinitionDto | null = null;
  referenceCreateId: string = "";
  isPatientQuery: boolean = false;

  get canSeeQueries() {
    return canSeePatientQueries() && currentUserModule.hasRight(Rights.QueryGetMyForPatientCmd);
  }

  get patientMyQueries() {
    return queryModule.queriesForPatientMy ?? [];
  }

  get myVisibleQueryTypeDefinitions(): Array<QueryTypeDefinitionDto> {
    return studyDefinitionModule.myVisibleQueryTypeDefinitions;
  }

  get myCreatableQueryTypeDefinitions(): Array<QueryTypeDefinitionDto> {
    return studyDefinitionModule.myCreatableQueryTypeDefinitions;
  }

  get hasCreatableQueryDefinitions() {
    return canSeeQueries && this.myCreatableQueryTypeDefinitions.length > 0;
  }

  get patientQueries(): IQueryListResponse {
    return queryModule.patientQueryListResponse;
  }

  get hasVisibleQueries() {
    return canSeeQueries && (this.patientQueries.items ?? []).length > 0;
  }

  handleCreateQuery(queryDefinition: IQueryTypeDefinitionDto, referenceId: string, isPatientQuery: boolean) {
    this.createQueryDefinition = deepCopy(queryDefinition);
    this.referenceCreateId = referenceId;
    this.isPatientQuery = isPatientQuery;
    this.showCreateQuery = true;
  }

  async handleSaveCreateQuery(queryCreate: IQueryCreateDto) {
    if (this.isPatientQuery)
      await queryModule.createQueryForPatient({patientId: this.referenceCreateId, createDto: queryCreate})
    else {
      await queryModule.createQueryForVisit({visitId: this.referenceCreateId, createDto: queryCreate})
    }
    await queryModule.loadPatientMyQueries(this.patientId);
    this.showCreateQuery = false;
  }
}
